import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { authGet, authPut, authPost, authDelete } from '../../services/apiService';
import { syncServiceNowAlerts, resetServiceNowIncidents, resetServiceNowAlerts } from '../../services/apiService';
import { format } from 'date-fns';

/**
 * ServiceNowAdmin component for managing ServiceNow configuration and sync
 * @returns {JSX.Element} ServiceNowAdmin component
 */
const ServiceNowAdmin = () => {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingIncidentRemediation, setSyncingIncidentRemediation] = useState(false);
  const [savingIncidentRemediationQuery, setSavingIncidentRemediationQuery] = useState(false);
  const [syncingAlerts, setSyncingAlerts] = useState(false);
  const [resettingIncidents, setResettingIncidents] = useState(false);
  const [resettingAlerts, setResettingAlerts] = useState(false);
  
  // Configuration state
  const [config, setConfig] = useState({
    instanceUrl: '',
    username: '',
    password: '',
    authMethod: 'basic',
    clientId: '',
    clientSecret: '',
    autoSyncIntervalHours: 4
  });
  
  // Track if config has been saved (has credentials stored in backend)
  const [configSaved, setConfigSaved] = useState(false);
  
  // Sync options
  const [syncOptions, setSyncOptions] = useState({
    query: '',
    limit: 1000,
    days: 550
  });

  // Alert sync options
  const [alertSyncOptions, setAlertSyncOptions] = useState({
    query: '',
    days: 550
  });

  // Incident Remediation query (own collection; separate from Major Incidents)
  const [incidentRemediationQuery, setIncidentRemediationQuery] = useState('');
  const DEFAULT_INCIDENT_REMEDIATION_QUERY =
    'opened_at>=2026-01-01^business_service.nameLIKEhighq^problem_idISNOTEMPTY';
  
  // Saving query state
  const [savingQuery, setSavingQuery] = useState(false);
  const [savingAlertQuery, setSavingAlertQuery] = useState(false);
  /** Inline feedback under Sync Incidents / Sync Alerts (top page alerts are easy to miss when accordion is open) */
  const [incidentQueryFeedback, setIncidentQueryFeedback] = useState(null);
  const [incidentRemediationQueryFeedback, setIncidentRemediationQueryFeedback] = useState(null);
  const [alertQueryFeedback, setAlertQueryFeedback] = useState(null);
  
  // Messages
  const [configSuccess, setConfigSuccess] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [incidentRemediationSyncResult, setIncidentRemediationSyncResult] = useState(null);
  const [alertSyncResult, setAlertSyncResult] = useState(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetAlertsConfirmOpen, setResetAlertsConfirmOpen] = useState(false);
  const [freshSyncConfirmOpen, setFreshSyncConfirmOpen] = useState(false);
  const [freshSyncAlertsConfirmOpen, setFreshSyncAlertsConfirmOpen] = useState(false);
  const [freshSyncing, setFreshSyncing] = useState(false);
  const [freshSyncingAlerts, setFreshSyncingAlerts] = useState(false);

  // Dark green color matching the theme
  const darkGreenColor = "#1D4434";

  // Fetch configuration
  const fetchConfig = async () => {
    try {
      setLoadingConfig(true);
      setConfigError(null);
      const response = await authGet('/api/servicenow/config');
      
      if (response.success) {
        const hasConfig = !!(response.config.instanceUrl && response.config.username);
        setConfigSaved(hasConfig);
        setConfig({
          instanceUrl: response.config.instanceUrl || '',
          username: response.config.username || '',
          password: '', // Don't show existing password
          authMethod: response.config.authMethod || 'basic',
          clientId: response.config.clientId || '',
          clientSecret: '', // Don't show existing secret
          autoSyncIntervalHours: response.config.autoSyncIntervalHours || 4
        });
        // Load saved queries
        setSyncOptions(prev => ({
          ...prev,
          query: response.config.savedQuery || ''
        }));
        setAlertSyncOptions(prev => ({
          ...prev,
          query: response.config.savedAlertQuery || ''
        }));
        setIncidentRemediationQuery(
          response.config.savedIncidentRemediationQuery || DEFAULT_INCIDENT_REMEDIATION_QUERY
        );
      } else {
        setConfigError('Failed to load configuration');
        setConfigSaved(false);
      }
    } catch (err) {
      console.error('Error fetching ServiceNow config:', err);
      setConfigError('Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Save configuration
  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      setConfigError(null);
      setConfigSuccess(null);
      
      // Ensure authMethod is always sent
      const configToSave = {
        ...config,
        authMethod: config.authMethod || 'basic'
      };
      
      console.log('Saving ServiceNow config:', {
        instanceUrl: configToSave.instanceUrl,
        username: configToSave.username,
        authMethod: configToSave.authMethod,
        hasPassword: !!configToSave.password,
        hasClientId: !!configToSave.clientId
      });
      
      const response = await authPut('/api/servicenow/config', configToSave);
      
      if (response.success) {
        setConfigSuccess('Configuration saved successfully');
        setConfigSaved(true); // Mark as saved
        // Don't clear password/secret fields - keep them for testing
        // Only clear if user explicitly wants to change them
        setTimeout(() => setConfigSuccess(null), 5000);
      } else {
        setConfigError(response.message || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Error saving ServiceNow config:', err);
      setConfigError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setConnectionTestResult(null);
      
      const response = await authPost('/api/servicenow/test-connection');
      
      setConnectionTestResult({
        success: response.success,
        message: response.message
      });
    } catch (err) {
      console.error('Error testing ServiceNow connection:', err);
      setConnectionTestResult({
        success: false,
        message: err.response?.data?.message || 'Failed to test connection'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  // Save query
  const handleSaveQuery = async () => {
    try {
      setSavingQuery(true);
      setConfigError(null);
      setConfigSuccess(null);
      setIncidentQueryFeedback(null);

      const response = await authPut('/api/servicenow/config/queries', {
        query: syncOptions.query
      });

      if (response.success) {
        const msg = 'Query saved successfully';
        setConfigSuccess(msg);
        setIncidentQueryFeedback({ severity: 'success', message: msg });
        setTimeout(() => setConfigSuccess(null), 5000);
        setTimeout(() => setIncidentQueryFeedback(null), 8000);
      } else {
        const msg = response.message || 'Failed to save query';
        setConfigError(msg);
        setIncidentQueryFeedback({ severity: 'error', message: msg });
      }
    } catch (err) {
      console.error('Error saving query:', err);
      const msg = err.message || 'Failed to save query';
      setConfigError(msg);
      setIncidentQueryFeedback({ severity: 'error', message: msg });
    } finally {
      setSavingQuery(false);
    }
  };

  // Save alert query
  const handleSaveAlertQuery = async () => {
    try {
      setSavingAlertQuery(true);
      setConfigError(null);
      setConfigSuccess(null);
      setAlertQueryFeedback(null);

      const response = await authPut('/api/servicenow/config/queries', {
        alertQuery: alertSyncOptions.query
      });

      if (response.success) {
        const msg = 'Alert query saved successfully';
        setConfigSuccess(msg);
        setAlertQueryFeedback({ severity: 'success', message: msg });
        setTimeout(() => setConfigSuccess(null), 5000);
        setTimeout(() => setAlertQueryFeedback(null), 8000);
      } else {
        const msg = response.message || 'Failed to save alert query';
        setConfigError(msg);
        setAlertQueryFeedback({ severity: 'error', message: msg });
      }
    } catch (err) {
      console.error('Error saving alert query:', err);
      const msg = err.message || 'Failed to save alert query';
      setConfigError(msg);
      setAlertQueryFeedback({ severity: 'error', message: msg });
    } finally {
      setSavingAlertQuery(false);
    }
  };

  // Sync incidents
  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      
      const response = await authPost('/api/servicenow/sync', syncOptions);
      
      if (response.success) {
        setSyncResult({
          success: true,
          message: response.message,
          summary: response.summary
        });
      } else {
        setSyncResult({
          success: false,
          message: response.message || 'Sync failed'
        });
      }
    } catch (err) {
      console.error('Error syncing ServiceNow incidents:', err);
      setSyncResult({
        success: false,
        message: err.response?.data?.message || 'Failed to sync incidents'
      });
    } finally {
      setSyncing(false);
    }
  };

  // Save Incident Remediation query (does not change Major Incidents savedQuery)
  const handleSaveIncidentRemediationQuery = async () => {
    try {
      setSavingIncidentRemediationQuery(true);
      setConfigError(null);
      setConfigSuccess(null);
      setIncidentRemediationQueryFeedback(null);

      const response = await authPut('/api/servicenow/config/queries', {
        incidentRemediationQuery
      });

      if (response.success) {
        const msg = 'Incident Remediation query saved successfully';
        setConfigSuccess(msg);
        setIncidentRemediationQueryFeedback({ severity: 'success', message: msg });
        setTimeout(() => setConfigSuccess(null), 5000);
        setTimeout(() => setIncidentRemediationQueryFeedback(null), 8000);
      } else {
        const msg = response.message || 'Failed to save Incident Remediation query';
        setConfigError(msg);
        setIncidentRemediationQueryFeedback({ severity: 'error', message: msg });
      }
    } catch (err) {
      console.error('Error saving Incident Remediation query:', err);
      const msg = err.message || 'Failed to save Incident Remediation query';
      setConfigError(msg);
      setIncidentRemediationQueryFeedback({ severity: 'error', message: msg });
    } finally {
      setSavingIncidentRemediationQuery(false);
    }
  };

  // Sync Incident Remediation Problems into their own collection (not ServiceNowTicket)
  const handleSyncIncidentRemediation = async () => {
    try {
      setSyncingIncidentRemediation(true);
      setIncidentRemediationSyncResult(null);

      const response = await authPost('/api/incident-remediation/sync', {
        query: incidentRemediationQuery
      });

      if (response.success) {
        setIncidentRemediationSyncResult({
          success: true,
          message: response.message,
          summary: response.summary
        });
      } else {
        setIncidentRemediationSyncResult({
          success: false,
          message: response.message || 'Incident Remediation sync failed'
        });
      }
    } catch (err) {
      console.error('Error syncing Incident Remediation problems:', err);
      setIncidentRemediationSyncResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to sync Incident Remediation'
      });
    } finally {
      setSyncingIncidentRemediation(false);
    }
  };

  // Sync alerts
  const handleSyncAlerts = async () => {
    try {
      setSyncingAlerts(true);
      setAlertSyncResult(null);
      
      const response = await syncServiceNowAlerts(alertSyncOptions);
      
      if (response.success) {
        setAlertSyncResult({
          success: true,
          message: response.message,
          summary: response.summary
        });
      } else {
        setAlertSyncResult({
          success: false,
          message: response.message || 'Sync failed'
        });
      }
    } catch (err) {
      console.error('Error syncing ServiceNow alerts:', err);
      setAlertSyncResult({
        success: false,
        message: err.response?.data?.message || 'Failed to sync alerts'
      });
    } finally {
      setSyncingAlerts(false);
    }
  };

  // Fresh sync incidents (delete all + re-sync)
  const handleFreshSync = async () => {
    try {
      setFreshSyncing(true);
      setFreshSyncConfirmOpen(false);
      setSyncResult(null);
      const response = await authPost('/api/servicenow/sync', { ...syncOptions, fresh: true });
      setSyncResult({
        success: response.success,
        message: response.success ? `Fresh sync complete. ${response.message}` : (response.message || 'Fresh sync failed'),
        summary: response.summary
      });
    } catch (err) {
      console.error('Error during fresh sync:', err);
      setSyncResult({
        success: false,
        message: err.response?.data?.message || 'Fresh sync failed'
      });
    } finally {
      setFreshSyncing(false);
    }
  };

  // Fresh sync alerts (delete all + re-sync)
  const handleFreshSyncAlerts = async () => {
    try {
      setFreshSyncingAlerts(true);
      setFreshSyncAlertsConfirmOpen(false);
      setAlertSyncResult(null);
      const response = await syncServiceNowAlerts({ ...alertSyncOptions, fresh: true });
      setAlertSyncResult({
        success: response.success,
        message: response.success ? `Fresh sync complete. ${response.message}` : (response.message || 'Fresh sync failed'),
        summary: response.summary
      });
    } catch (err) {
      console.error('Error during fresh alerts sync:', err);
      setAlertSyncResult({
        success: false,
        message: err.response?.data?.message || 'Fresh sync failed'
      });
    } finally {
      setFreshSyncingAlerts(false);
    }
  };

  // Reset incidents table
  const handleResetIncidents = async () => {
    try {
      setResettingIncidents(true);
      setConfigError(null);
      setConfigSuccess(null);
      
      const response = await resetServiceNowIncidents();
      
      if (response.success) {
        setConfigSuccess(`Successfully reset incidents table. Deleted ${response.deletedCount || 0} incident(s).`);
        setTimeout(() => setConfigSuccess(null), 5000);
        setResetConfirmOpen(false);
      } else {
        setConfigError(response.message || 'Failed to reset incidents table');
        setResetConfirmOpen(false);
      }
    } catch (error) {
      console.error('Error resetting incidents:', error);
      setConfigError(error.response?.data?.message || 'Failed to reset incidents table');
      setResetConfirmOpen(false);
    } finally {
      setResettingIncidents(false);
    }
  };

  // Reset alerts table
  const handleResetAlerts = async () => {
    try {
      setResettingAlerts(true);
      setConfigError(null);
      setConfigSuccess(null);
      
      const response = await resetServiceNowAlerts();
      
      if (response.success) {
        setConfigSuccess(`Successfully reset alerts table. Deleted ${response.deletedCount || 0} alert(s).`);
        setTimeout(() => setConfigSuccess(null), 5000);
        setResetAlertsConfirmOpen(false);
      } else {
        setConfigError(response.message || 'Failed to reset alerts table');
        setResetAlertsConfirmOpen(false);
      }
    } catch (error) {
      console.error('Error resetting alerts:', error);
      setConfigError(error.response?.data?.message || 'Failed to reset alerts table');
      setResetAlertsConfirmOpen(false);
    } finally {
      setResettingAlerts(false);
    }
  };

  const clearMessages = () => {
    setConfigSuccess(null);
    setConfigError(null);
    setConnectionTestResult(null);
    setSyncResult(null);
    setAlertSyncResult(null);
    setIncidentQueryFeedback(null);
    setAlertQueryFeedback(null);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: darkGreenColor }}>
        ServiceNow Integration
      </Typography>

      {/* Messages */}
      {configSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setConfigSuccess(null)}>
          {configSuccess}
        </Alert>
      )}
      {configError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConfigError(null)}>
          {configError}
        </Alert>
      )}

      {/* Configuration Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon sx={{ mr: 1, color: darkGreenColor }} />
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            ServiceNow Configuration
          </Typography>
        </Box>

        {loadingConfig ? (
          <LinearProgress sx={{ mb: 2 }} />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Instance URL"
                value={config.instanceUrl}
                onChange={(e) => setConfig({...config, instanceUrl: e.target.value})}
                placeholder="https://yourinstance.service-now.com"
                fullWidth
                required
                helperText="Your ServiceNow instance URL (without trailing slash)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                value={config.username}
                onChange={(e) => setConfig({...config, username: e.target.value})}
                fullWidth
                required
                helperText="ServiceNow API user username"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Authentication Method</InputLabel>
                <Select
                  value={config.authMethod}
                  label="Authentication Method"
                  onChange={(e) => setConfig({...config, authMethod: e.target.value})}
                >
                  <MenuItem value="basic">Basic Auth (Username/Password)</MenuItem>
                  <MenuItem value="oauth2">OAuth2 Password Grant (Username/Password/Client ID/Secret)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {config.authMethod === 'basic' ? (
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type="password"
                  value={config.password}
                  onChange={(e) => setConfig({...config, password: e.target.value})}
                  fullWidth
                  helperText="Leave blank to keep existing password"
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      OAuth2 Password Grant requires username, password, client ID, and client secret.
                      The username field above is used for OAuth2 authentication.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Client ID"
                    value={config.clientId}
                    onChange={(e) => setConfig({...config, clientId: e.target.value})}
                    fullWidth
                    required
                    helperText="OAuth2 Client ID"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Client Secret"
                    type="password"
                    value={config.clientSecret}
                    onChange={(e) => setConfig({...config, clientSecret: e.target.value})}
                    fullWidth
                    required
                    helperText="OAuth2 Client Secret (leave blank to keep existing)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Password (for OAuth2)"
                    type="password"
                    value={config.password}
                    onChange={(e) => setConfig({...config, password: e.target.value})}
                    fullWidth
                    helperText="ServiceNow user password for OAuth2 password grant (leave blank to keep existing)"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                label="Auto Sync Interval"
                type="number"
                value={config.autoSyncIntervalHours}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 4;
                  if (value >= 1 && value <= 168) {
                    setConfig({...config, autoSyncIntervalHours: value});
                  }
                }}
                fullWidth
                inputProps={{ min: 1, max: 168, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>
                }}
                helperText="Hours between automatic syncs (1-168, default: 4)"
                disabled={loadingConfig || savingConfig}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={testingConnection ? <RefreshIcon /> : <CheckCircleIcon />}
                  onClick={handleTestConnection}
                  disabled={
                    testingConnection || 
                    !config.instanceUrl || 
                    !config.username || 
                    (config.authMethod === 'oauth2' && !config.clientId && !configSaved) ||
                    (config.authMethod === 'basic' && !config.password && !configSaved)
                  }
                  sx={{
                    borderColor: darkGreenColor,
                    color: darkGreenColor,
                    '&:hover': {
                      borderColor: darkGreenColor,
                      bgcolor: `${darkGreenColor}10`
                    }
                  }}
                >
                  {testingConnection ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveConfig}
                  disabled={savingConfig || !config.instanceUrl || !config.username ||
                    (config.authMethod === 'oauth2' && (!config.clientId || (!config.password && !config.clientSecret)))}
                  sx={{
                    bgcolor: darkGreenColor,
                    '&:hover': {
                      bgcolor: '#2d5a47'
                    }
                  }}
                >
                  {savingConfig ? 'Saving...' : 'Save Configuration'}
                </Button>
              </Box>
            </Grid>
            {connectionTestResult && (
              <Grid item xs={12}>
                <Alert 
                  severity={connectionTestResult.success ? 'success' : 'error'}
                  icon={connectionTestResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                  onClose={() => setConnectionTestResult(null)}
                >
                  {connectionTestResult.message}
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {/* Sync Section */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <SyncIcon sx={{ mr: 1, color: darkGreenColor }} />
              <Typography variant="h6" sx={{ color: darkGreenColor }}>
                Sync Incidents
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="ServiceNow Query"
                  value={syncOptions.query}
                  onChange={(e) => setSyncOptions({...syncOptions, query: e.target.value})}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Major Incidents only. Leave empty to use saved query or the HighQ major incidents default."
                  placeholder="major_incident_state=accepted^opened_at>=2026-01-01^business_service.nameLIKEhighq"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Days to Look Back"
                  type="number"
                  value={syncOptions.days}
                  onChange={(e) => setSyncOptions({...syncOptions, days: parseInt(e.target.value) || 550})}
                  fullWidth
                  inputProps={{ min: 1, max: 1825 }}
                  helperText="550 = from Jan 2025 · 365 = last 1 year"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Batch Size"
                  type="number"
                  value={syncOptions.limit}
                  onChange={(e) => setSyncOptions({...syncOptions, limit: parseInt(e.target.value) || 1000})}
                  fullWidth
                  inputProps={{ min: 100, max: 10000 }}
                  helperText="Number of records per batch"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveQuery}
                    disabled={savingQuery}
                    sx={{
                      borderColor: darkGreenColor,
                      color: darkGreenColor,
                      '&:hover': {
                        borderColor: darkGreenColor,
                        bgcolor: `${darkGreenColor}10`
                      }
                    }}
                  >
                    {savingQuery ? 'Saving...' : 'Save Query'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    onClick={handleSync}
                    disabled={syncing || freshSyncing}
                    sx={{
                      bgcolor: darkGreenColor,
                      '&:hover': {
                        bgcolor: '#2d5a47'
                      }
                    }}
                  >
                    {syncing ? 'Syncing...' : 'Sync Incidents'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => setFreshSyncConfirmOpen(true)}
                    disabled={syncing || freshSyncing}
                    sx={{
                      borderColor: '#e65100',
                      color: '#e65100',
                      '&:hover': {
                        borderColor: '#e65100',
                        bgcolor: '#e6510010'
                      }
                    }}
                  >
                    {freshSyncing ? 'Syncing...' : 'Fresh Sync'}
                  </Button>
                </Box>
              </Grid>
              {incidentQueryFeedback && (
                <Grid item xs={12}>
                  <Alert
                    severity={incidentQueryFeedback.severity}
                    onClose={() => setIncidentQueryFeedback(null)}
                    sx={{ mt: 0 }}
                  >
                    {incidentQueryFeedback.message}
                  </Alert>
                </Grid>
              )}
              {(syncing || freshSyncing) && (
                <Grid item xs={12}>
                  <LinearProgress />
                  {freshSyncing && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Deleting existing incidents and re-syncing from ServiceNow…
                    </Typography>
                  )}
                </Grid>
              )}
              {syncResult && (
                <Grid item xs={12}>
                  <Alert 
                    severity={syncResult.success ? 'success' : 'error'}
                    onClose={() => setSyncResult(null)}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {syncResult.message}
                    </Typography>
                    {syncResult.summary && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {syncResult.summary.deleted != null && (
                          <Chip label={`Deleted: ${syncResult.summary.deleted}`} color="warning" size="small" />
                        )}
                        <Chip label={`Total: ${syncResult.summary.total}`} color="primary" size="small" />
                        <Chip label={`Created: ${syncResult.summary.created}`} color="success" size="small" />
                        <Chip label={`Updated: ${syncResult.summary.updated}`} color="info" size="small" />
                        {syncResult.summary.errors > 0 && (
                          <Chip label={`Errors: ${syncResult.summary.errors}`} color="error" size="small" />
                        )}
                      </Box>
                    )}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Reset incidents table to clear all synced incidents
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setResetConfirmOpen(true)}
                    disabled={resettingIncidents}
                    sx={{
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#d32f2f',
                        bgcolor: '#d32f2f10'
                      }
                    }}
                  >
                    {resettingIncidents ? 'Resetting...' : 'Reset Incidents Table'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Incident Remediation — own collection; separate from Major Incidents */}
      <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <SyncIcon sx={{ mr: 1, color: darkGreenColor }} />
              <Typography variant="h6" sx={{ color: darkGreenColor }}>
                Sync Incident Remediation
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Re-reads HighQ Problems into the Incident Remediation collection only. Does not change Major Incidents.
                  Uses the same ServiceNow connection. Requires Operational Excellence access. Auto-sync uses the saved query after scheduled Major Incidents sync.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="ServiceNow Query"
                  value={incidentRemediationQuery}
                  onChange={(e) => setIncidentRemediationQuery(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Incidents that have a Problem. Leave empty on save to fall back to the 2026 HighQ + problem_id default."
                  placeholder={DEFAULT_INCIDENT_REMEDIATION_QUERY}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveIncidentRemediationQuery}
                    disabled={savingIncidentRemediationQuery}
                    sx={{
                      borderColor: darkGreenColor,
                      color: darkGreenColor,
                      '&:hover': {
                        borderColor: darkGreenColor,
                        bgcolor: `${darkGreenColor}10`
                      }
                    }}
                  >
                    {savingIncidentRemediationQuery ? 'Saving...' : 'Save Query'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    onClick={handleSyncIncidentRemediation}
                    disabled={syncingIncidentRemediation}
                    sx={{
                      bgcolor: darkGreenColor,
                      '&:hover': {
                        bgcolor: '#2d5a47'
                      }
                    }}
                  >
                    {syncingIncidentRemediation ? 'Syncing...' : 'Sync Incident Remediation'}
                  </Button>
                </Box>
              </Grid>
              {incidentRemediationQueryFeedback && (
                <Grid item xs={12}>
                  <Alert
                    severity={incidentRemediationQueryFeedback.severity}
                    onClose={() => setIncidentRemediationQueryFeedback(null)}
                  >
                    {incidentRemediationQueryFeedback.message}
                  </Alert>
                </Grid>
              )}
              {syncingIncidentRemediation && (
                <Grid item xs={12}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Syncing Problems from ServiceNow into Incident Remediation…
                  </Typography>
                </Grid>
              )}
              {incidentRemediationSyncResult && (
                <Grid item xs={12}>
                  <Alert
                    severity={incidentRemediationSyncResult.success ? 'success' : 'error'}
                    onClose={() => setIncidentRemediationSyncResult(null)}
                  >
                    <Typography variant="body1" sx={{ mb: incidentRemediationSyncResult.summary ? 1 : 0 }}>
                      {incidentRemediationSyncResult.message}
                    </Typography>
                    {incidentRemediationSyncResult.summary && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`Incidents: ${incidentRemediationSyncResult.summary.incidents}`} color="primary" size="small" />
                        <Chip label={`Problems: ${incidentRemediationSyncResult.summary.problems}`} color="success" size="small" />
                        {incidentRemediationSyncResult.summary.missing > 0 && (
                          <Chip label={`Missing: ${incidentRemediationSyncResult.summary.missing}`} color="warning" size="small" />
                        )}
                        {incidentRemediationSyncResult.summary.removed > 0 && (
                          <Chip label={`Removed: ${incidentRemediationSyncResult.summary.removed}`} color="info" size="small" />
                        )}
                      </Box>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Sync Alerts Section */}
      <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <SyncIcon sx={{ mr: 1, color: darkGreenColor }} />
              <Typography variant="h6" sx={{ color: darkGreenColor }}>
                Sync Alerts
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="ServiceNow Query"
                  value={alertSyncOptions.query}
                  onChange={(e) => setAlertSyncOptions({...alertSyncOptions, query: e.target.value})}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Leave empty to use saved query or the HighQ major incidents default."
                  placeholder="major_incident_state=accepted^opened_at>=2026-01-01^business_service.nameLIKEhighq"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Days to Look Back"
                  type="number"
                  value={alertSyncOptions.days}
                  onChange={(e) => setAlertSyncOptions({...alertSyncOptions, days: parseInt(e.target.value) || 550})}
                  fullWidth
                  inputProps={{ min: 1, max: 1825 }}
                  helperText="550 = from Jan 2025 · 365 = last 1 year"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Alerts sync will fetch only the top 500 incidents matching your query (no pagination).
                  </Typography>
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveAlertQuery}
                    disabled={savingAlertQuery}
                    sx={{
                      borderColor: darkGreenColor,
                      color: darkGreenColor,
                      '&:hover': {
                        borderColor: darkGreenColor,
                        bgcolor: `${darkGreenColor}10`
                      }
                    }}
                  >
                    {savingAlertQuery ? 'Saving...' : 'Save Query'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    onClick={handleSyncAlerts}
                    disabled={syncingAlerts || freshSyncingAlerts}
                    sx={{
                      bgcolor: darkGreenColor,
                      '&:hover': {
                        bgcolor: '#2d5a47'
                      }
                    }}
                  >
                    {syncingAlerts ? 'Syncing...' : 'Sync Alerts'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => setFreshSyncAlertsConfirmOpen(true)}
                    disabled={syncingAlerts || freshSyncingAlerts}
                    sx={{
                      borderColor: '#e65100',
                      color: '#e65100',
                      '&:hover': {
                        borderColor: '#e65100',
                        bgcolor: '#e6510010'
                      }
                    }}
                  >
                    {freshSyncingAlerts ? 'Syncing...' : 'Fresh Sync'}
                  </Button>
                </Box>
              </Grid>
              {alertQueryFeedback && (
                <Grid item xs={12}>
                  <Alert
                    severity={alertQueryFeedback.severity}
                    onClose={() => setAlertQueryFeedback(null)}
                    sx={{ mt: 0 }}
                  >
                    {alertQueryFeedback.message}
                  </Alert>
                </Grid>
              )}
              {(syncingAlerts || freshSyncingAlerts) && (
                <Grid item xs={12}>
                  <LinearProgress />
                  {freshSyncingAlerts && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Deleting existing alerts and re-syncing from ServiceNow…
                    </Typography>
                  )}
                </Grid>
              )}
              {alertSyncResult && (
                <Grid item xs={12}>
                  <Alert 
                    severity={alertSyncResult.success ? 'success' : 'error'}
                    onClose={() => setAlertSyncResult(null)}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {alertSyncResult.message}
                    </Typography>
                    {alertSyncResult.summary && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {alertSyncResult.summary.deleted != null && (
                          <Chip label={`Deleted: ${alertSyncResult.summary.deleted}`} color="warning" size="small" />
                        )}
                        <Chip label={`Total: ${alertSyncResult.summary.total}`} color="primary" size="small" />
                        <Chip label={`Created: ${alertSyncResult.summary.created}`} color="success" size="small" />
                        <Chip label={`Updated: ${alertSyncResult.summary.updated}`} color="info" size="small" />
                        {alertSyncResult.summary.errors > 0 && (
                          <Chip label={`Errors: ${alertSyncResult.summary.errors}`} color="error" size="small" />
                        )}
                      </Box>
                    )}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Reset alerts table to clear all synced alerts
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setResetAlertsConfirmOpen(true)}
                    disabled={resettingAlerts}
                    sx={{
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#d32f2f',
                        bgcolor: '#d32f2f10'
                      }
                    }}
                  >
                    {resettingAlerts ? 'Resetting...' : 'Reset Alerts Table'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Reset Incidents Confirmation Dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => !resettingIncidents && setResetConfirmOpen(false)}
        aria-labelledby="reset-incidents-dialog-title"
        aria-describedby="reset-incidents-dialog-description"
      >
        <DialogTitle id="reset-incidents-dialog-title" sx={{ color: '#d32f2f' }}>
          Reset Incidents Table
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-incidents-dialog-description">
            Are you sure you want to reset the incidents table? This will:
            <br /><br />
            <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
              <li>Delete all synced incidents from the database</li>
            </ul>
            <br />
            <strong>This action cannot be undone.</strong>
            <br />
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Note: This only deletes the synced incidents from the database. It does not affect ServiceNow.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetConfirmOpen(false)} 
            disabled={resettingIncidents}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetIncidents} 
            color="error" 
            variant="contained"
            disabled={resettingIncidents}
            startIcon={resettingIncidents ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {resettingIncidents ? 'Resetting...' : 'Reset Incidents Table'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fresh Sync Incidents Confirmation Dialog */}
      <Dialog
        open={freshSyncConfirmOpen}
        onClose={() => !freshSyncing && setFreshSyncConfirmOpen(false)}
        aria-labelledby="fresh-sync-dialog-title"
      >
        <DialogTitle id="fresh-sync-dialog-title" sx={{ color: '#e65100' }}>
          Fresh Sync Incidents
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will <strong>delete all existing synced incidents</strong> from the database and then re-sync using the current query. Use this to start clean with the correct HighQ-filtered data.
            <br /><br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreshSyncConfirmOpen(false)} disabled={freshSyncing} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleFreshSync}
            variant="contained"
            disabled={freshSyncing}
            startIcon={freshSyncing ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}
          >
            {freshSyncing ? 'Syncing...' : 'Delete & Re-Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fresh Sync Alerts Confirmation Dialog */}
      <Dialog
        open={freshSyncAlertsConfirmOpen}
        onClose={() => !freshSyncingAlerts && setFreshSyncAlertsConfirmOpen(false)}
        aria-labelledby="fresh-sync-alerts-dialog-title"
      >
        <DialogTitle id="fresh-sync-alerts-dialog-title" sx={{ color: '#e65100' }}>
          Fresh Sync Alerts
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will <strong>delete all existing synced alerts</strong> from the database and then re-sync using the current query. Use this to start clean with the correct HighQ-filtered data.
            <br /><br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreshSyncAlertsConfirmOpen(false)} disabled={freshSyncingAlerts} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleFreshSyncAlerts}
            variant="contained"
            disabled={freshSyncingAlerts}
            startIcon={freshSyncingAlerts ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}
          >
            {freshSyncingAlerts ? 'Syncing...' : 'Delete & Re-Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Alerts Confirmation Dialog */}
      <Dialog
        open={resetAlertsConfirmOpen}
        onClose={() => !resettingAlerts && setResetAlertsConfirmOpen(false)}
        aria-labelledby="reset-alerts-dialog-title"
        aria-describedby="reset-alerts-dialog-description"
      >
        <DialogTitle id="reset-alerts-dialog-title" sx={{ color: '#d32f2f' }}>
          Reset Alerts Table
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-alerts-dialog-description">
            Are you sure you want to reset the alerts table? This will:
            <br /><br />
            <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
              <li>Delete all synced alerts from the database</li>
            </ul>
            <br />
            <strong>This action cannot be undone.</strong>
            <br />
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Note: This only deletes the synced alerts from the database. It does not affect ServiceNow.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetAlertsConfirmOpen(false)} 
            disabled={resettingAlerts}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetAlerts} 
            color="error" 
            variant="contained"
            disabled={resettingAlerts}
            startIcon={resettingAlerts ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {resettingAlerts ? 'Resetting...' : 'Reset Alerts Table'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceNowAdmin;

